/**
 * Contract Service (Frontend)
 * Read-only operations for contract data via Supabase
 * Write operations via Python backend API
 *
 * Write operations (send, cancel, log events) are handled by Python backend
 * See: joules-admin-backend/app/api/v1/endpoints/contracts.py
 *
 * Epic: CONTRACTS-REDESIGN
 * Task: CONTRACTS-0.4
 */

import { supabase } from '@/lib/supabaseClient';
import {
  Contract,
  ContractEvent,
  ContractEventType,
  ContractWithHistory,
  ContractField,
  NewContractField,
  ContractFieldPatch,
  ContractSigner,
  RecipientType,
  StoredSignature,
  SaveSignaturePayload,
  SignatureOptions,
  DeliverabilityStatus,
  SealedRecord,
} from '@/types/contract';
import { SigningContextV2, SigningFieldV2, ContractTemplate, SaveTemplateInput, CreateFromTemplateInput } from '@/types/contracts';
import { groupSignersByContract, type SignersByContract } from '@/lib/contractStatus';
import type { BuiltDocNode } from '@/lib/builderSchema';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://admin-api.joulescrm.com';

/**
 * Result type for service operations
 */
interface ServiceResult<T = void> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Get auth token for backend requests
 */
async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

class ContractService {
  // ============================================
  // READ OPERATIONS (Direct Supabase)
  // ============================================

  /**
   * Get all contracts for a project
   */
  async getProjectContracts(projectId: string): Promise<ServiceResult<Contract[]>> {
    try {
      const { data, error } = await supabase
        .from('contracts')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching project contracts:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as Contract[] };
    } catch (error) {
      console.error('Error in getProjectContracts:', error);
      return { success: false, error: 'Failed to fetch contracts' };
    }
  }

  /**
   * Get a single contract by ID
   */
  async getContract(contractId: string): Promise<ServiceResult<Contract>> {
    try {
      const { data, error } = await supabase
        .from('contracts')
        .select('*')
        .eq('id', contractId)
        .single();

      if (error) {
        console.error('Error fetching contract:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as Contract };
    } catch (error) {
      console.error('Error in getContract:', error);
      return { success: false, error: 'Failed to fetch contract' };
    }
  }

  /**
   * Get contract with its event history
   */
  async getContractWithHistory(contractId: string): Promise<ServiceResult<ContractWithHistory>> {
    try {
      // Fetch contract and events in parallel
      const [contractResult, eventsResult] = await Promise.all([
        supabase.from('contracts').select('*').eq('id', contractId).single(),
        supabase
          .from('contract_events')
          .select('*')
          .eq('contract_id', contractId)
          .order('created_at', { ascending: false }),
      ]);

      if (contractResult.error) {
        console.error('Error fetching contract:', contractResult.error);
        return { success: false, error: contractResult.error.message };
      }

      if (eventsResult.error) {
        console.error('Error fetching contract events:', eventsResult.error);
        // Don't fail if events table doesn't exist yet
        return {
          success: true,
          data: {
            contract: contractResult.data as Contract,
            events: [],
          },
        };
      }

      return {
        success: true,
        data: {
          contract: contractResult.data as Contract,
          events: eventsResult.data as ContractEvent[],
        },
      };
    } catch (error) {
      console.error('Error in getContractWithHistory:', error);
      return { success: false, error: 'Failed to fetch contract with history' };
    }
  }

  /**
   * Get contract events (history) for a contract
   */
  async getContractEvents(contractId: string): Promise<ServiceResult<ContractEvent[]>> {
    try {
      const { data, error } = await supabase
        .from('contract_events')
        .select('*')
        .eq('contract_id', contractId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching contract events:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as ContractEvent[] };
    } catch (error) {
      console.error('Error in getContractEvents:', error);
      return { success: false, error: 'Failed to fetch contract events' };
    }
  }

  /**
   * Get contracts for a project that are pending signature
   */
  async getPendingContracts(projectId: string): Promise<ServiceResult<Contract[]>> {
    try {
      const { data, error } = await supabase
        .from('contracts')
        .select('*')
        .eq('project_id', projectId)
        .in('status', ['Sent to Customer', 'Pending Signature 1', 'Pending Signature 2', 'Pending Signature 3'])
        .order('sent_at', { ascending: true });

      if (error) {
        console.error('Error fetching pending contracts:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as Contract[] };
    } catch (error) {
      console.error('Error in getPendingContracts:', error);
      return { success: false, error: 'Failed to fetch pending contracts' };
    }
  }

  /**
   * Get count of pending contracts for a project (for badges/filters)
   */
  async getPendingContractCount(projectId: string): Promise<ServiceResult<number>> {
    try {
      const { count, error } = await supabase
        .from('contracts')
        .select('*', { count: 'exact', head: true })
        .eq('project_id', projectId)
        .in('status', ['Sent to Customer', 'Pending Signature 1', 'Pending Signature 2', 'Pending Signature 3']);

      if (error) {
        console.error('Error fetching pending contract count:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: count || 0 };
    } catch (error) {
      console.error('Error in getPendingContractCount:', error);
      return { success: false, error: 'Failed to fetch pending contract count' };
    }
  }

  /**
   * Get contract summary for a project (for header display)
   */
  async getContractSummary(projectId: string): Promise<ServiceResult<{ total: number; signed: number }>> {
    try {
      const { data, error } = await supabase
        .from('contracts')
        .select('status')
        .eq('project_id', projectId)
        .not('status', 'eq', 'Canceled');

      if (error) {
        console.error('Error fetching contract summary:', error);
        return { success: false, error: error.message };
      }

      const total = data?.length || 0;
      const signed = data?.filter(c => c.status === 'Signed' || c.status === 'Complete').length || 0;

      return { success: true, data: { total, signed } };
    } catch (error) {
      console.error('Error in getContractSummary:', error);
      return { success: false, error: 'Failed to fetch contract summary' };
    }
  }

  /**
   * Subscribe to contract changes for a project (real-time)
   */
  subscribeToProjectContracts(
    projectId: string,
    callback: (contract: Contract) => void
  ): { unsubscribe: () => void } {
    const channel = supabase
      .channel(`contracts:project_id=${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contracts',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          if (payload.new) {
            callback(payload.new as Contract);
          }
        }
      )
      .subscribe();

    return {
      unsubscribe: () => {
        supabase.removeChannel(channel);
      },
    };
  }

  /**
   * Batch-fetch v2 recipients for a set of contracts (CB-7.1, Task 3). One query for the whole
   * project — never N+1 — keyed off the contract ids (contract_signers has no project_id, AD-3).
   * Returns a contract_id → signers map for per-recipient progress in the list. Tenant-scoped by
   * RLS on the authenticated client. Empty input ⇒ empty map (no query).
   */
  async getProjectContractSigners(
    contractIds: string[]
  ): Promise<ServiceResult<SignersByContract>> {
    if (contractIds.length === 0) {
      return { success: true, data: {} };
    }
    try {
      const { data, error } = await supabase
        .from('contract_signers')
        .select('*')
        .in('contract_id', contractIds)
        .order('signing_order', { ascending: true });

      if (error) {
        console.error('Error fetching project contract signers:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: groupSignersByContract((data ?? []) as ContractSigner[]) };
    } catch (error) {
      console.error('Error in getProjectContractSigners:', error);
      return { success: false, error: 'Failed to fetch contract recipients' };
    }
  }

  /**
   * Subscribe to v2 recipient changes for a project's contracts (CB-7.1, Task 4) so per-recipient
   * progress and the derived in_progress/completed display update live as signers act. Reuses the
   * existing channel/cleanup idiom (AD-17); filters by `contract_id=in.(...)` since contract_signers
   * has no project_id. Returns a no-op unsubscribe when there are no contracts to watch.
   */
  subscribeToProjectContractSigners(
    contractIds: string[],
    callback: (signer: ContractSigner, eventType: 'INSERT' | 'UPDATE' | 'DELETE') => void
  ): { unsubscribe: () => void } {
    if (contractIds.length === 0) {
      return { unsubscribe: () => {} };
    }
    const channel = supabase
      .channel(`contract_signers:project=${contractIds.join('|')}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contract_signers',
          filter: `contract_id=in.(${contractIds.join(',')})`,
        },
        (payload) => {
          // ⚠️ SUB-1.48 · F-107 · CORRECTED. This read: "CB-7 review P2: forward
          // DELETEs too (payload.old) so a removed recipient is reconciled out."
          // `contract_signers` was NOT in the supabase_realtime publication until
          // `20260819b8`, so none of this ran at all. Publishing it fixes INSERT
          // and UPDATE — but NOT the DELETE case the note claims.
          //
          // This subscription filters on `contract_id`. `contract_signers` is
          // REPLICA IDENTITY DEFAULT, so a DELETE's `old_record` carries ONLY the
          // primary key; `contract_id` is absent, the filter cannot be evaluated,
          // and Realtime DROPS the event. See "F-44 CORRECTED" in the epic's
          // security-findings.md.
          //
          // Live signer-removal would need REPLICA IDENTITY FULL here, which fans
          // every column of every deleted row to every subscriber with no RLS
          // check. That trade was deliberately not taken. Removal still reconciles
          // on the next refetch.
          // out — DELETE carries only the PK in `old`, which is all the consumer needs (by id).
          const row = (payload.new ?? payload.old) as ContractSigner | undefined;
          if (row) {
            callback(row, payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE');
          }
        }
      )
      .subscribe();

    return {
      unsubscribe: () => {
        supabase.removeChannel(channel);
      },
    };
  }

  /**
   * Subscribe to a single contract's row changes (CB-7.2) — keeps the detail-view envelope-status
   * badge live as the contract advances. Mirrors `subscribeToProjectContracts` but `id`-scoped.
   */
  subscribeToContract(
    contractId: string,
    callback: (contract: Contract) => void
  ): { unsubscribe: () => void } {
    const channel = supabase
      .channel(`contracts:id=${contractId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contracts',
          filter: `id=eq.${contractId}`,
        },
        (payload) => {
          if (payload.new) {
            callback(payload.new as Contract);
          }
        }
      )
      .subscribe();

    return {
      unsubscribe: () => {
        supabase.removeChannel(channel);
      },
    };
  }

  /**
   * Subscribe to one contract's recipient changes (CB-7.2) so per-signer "signed" ticks + delivery
   * status update live in the detail view (UX-DR11, AD-17). Same channel/cleanup idiom as the list
   * subscriptions; RLS is the security boundary, the filter is for relevance only.
   */
  subscribeToContractSigners(
    contractId: string,
    callback: (signer: ContractSigner, eventType: 'INSERT' | 'UPDATE' | 'DELETE') => void
  ): { unsubscribe: () => void } {
    const channel = supabase
      .channel(`contract_signers:contract=${contractId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contract_signers',
          filter: `contract_id=eq.${contractId}`,
        },
        (payload) => {
          // ⚠️ SUB-1.48 · F-107 · CORRECTED. This read: "CB-7 review P2: forward
          // DELETEs too (payload.old) so a removed recipient is reconciled out."
          // `contract_signers` was NOT in the supabase_realtime publication until
          // `20260819b8`, so none of this ran at all. Publishing it fixes INSERT
          // and UPDATE — but NOT the DELETE case the note claims.
          //
          // This subscription filters on `contract_id`. `contract_signers` is
          // REPLICA IDENTITY DEFAULT, so a DELETE's `old_record` carries ONLY the
          // primary key; `contract_id` is absent, the filter cannot be evaluated,
          // and Realtime DROPS the event. See "F-44 CORRECTED" in the epic's
          // security-findings.md.
          //
          // Live signer-removal would need REPLICA IDENTITY FULL here, which fans
          // every column of every deleted row to every subscriber with no RLS
          // check. That trade was deliberately not taken. Removal still reconciles
          // on the next refetch.
          const row = (payload.new ?? payload.old) as ContractSigner | undefined;
          if (row) {
            callback(row, payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE');
          }
        }
      )
      .subscribe();

    return {
      unsubscribe: () => {
        supabase.removeChannel(channel);
      },
    };
  }

  // ============================================
  // WRITE OPERATIONS (Python Backend API)
  // ============================================

  /**
   * Cancel a contract (via backend)
   */
  async cancelContract(
    contractId: string,
    reason?: string
  ): Promise<ServiceResult<Contract>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ reason }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.message || 'Failed to cancel contract' };
      }

      return { success: true, data: data.contract as Contract };
    } catch (error) {
      console.error('Error in cancelContract:', error);
      return { success: false, error: 'Failed to cancel contract' };
    }
  }

  /**
   * CB-6.6: fetch the sealed record for a completed v2 contract — time-limited signed URLs for the
   * sealed PDF + Certificate of Completion + the audit trail (authorized-internal download, FR-64).
   */
  async getSealedRecord(contractId: string): Promise<ServiceResult<SealedRecord>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await fetch(
        `${BACKEND_URL}/api/v1/contracts/${contractId}/sealed-record`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.detail || 'Failed to load the sealed record' };
      }
      return { success: true, data: data as SealedRecord };
    } catch (error) {
      console.error('Error in getSealedRecord:', error);
      return { success: false, error: 'Failed to load the sealed record' };
    }
  }

  /**
   * Resend contract email (via backend)
   */
  async resendContract(contractId: string): Promise<ServiceResult> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.message || 'Failed to resend contract' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in resendContract:', error);
      return { success: false, error: 'Failed to resend contract' };
    }
  }

  /**
   * Send reminder for a pending contract (via backend)
   */
  async sendReminder(
    contractId: string,
    customMessage?: string
  ): Promise<ServiceResult<{ reminder_count: number }>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/send-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          custom_message: customMessage,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.message || 'Failed to send reminder' };
      }

      return { success: true, data: { reminder_count: data.reminder_count } };
    } catch (error) {
      console.error('Error in sendReminder:', error);
      return { success: false, error: 'Failed to send reminder' };
    }
  }

  /**
   * Log a contract event (direct to Supabase)
   * Used for audit trail - tracks created, sent, viewed, etc.
   *
   * Note: This is fire-and-forget - callers should not await or handle errors
   * to avoid blocking main contract operations.
   */
  async logEvent(
    contractId: string,
    eventType: ContractEventType,
    eventData?: Record<string, unknown>
  ): Promise<ServiceResult<ContractEvent>> {
    try {
      // Get the contract to find tenant_id
      const { data: contract, error: contractError } = await supabase
        .from('contracts')
        .select('tenant_id')
        .eq('id', contractId)
        .single();

      if (contractError || !contract) {
        console.error('Error fetching contract for event logging:', contractError);
        return { success: false, error: 'Contract not found' };
      }

      // Get current user info for actor details
      const { data: { user } } = await supabase.auth.getUser();
      let actorName = 'System';
      let actorId = null;

      if (user) {
        actorId = user.id;
        // Get user name from users table
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single();

        if (!userError && userData) {
          actorName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || user.email || 'User';
        } else {
          actorName = user.email || 'User';
        }
      }

      // Insert the event directly
      const { data: event, error: insertError } = await supabase
        .from('contract_events')
        .insert({
          contract_id: contractId,
          event_type: eventType,
          event_data: eventData || {},
          actor_id: actorId,
          actor_type: 'user',
          actor_name: actorName,
          tenant_id: contract.tenant_id,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error inserting contract event:', insertError);
        return { success: false, error: insertError.message };
      }

      return { success: true, data: event as ContractEvent };
    } catch (error) {
      console.error('Error in logEvent:', error);
      return { success: false, error: 'Failed to log event' };
    }
  }

  // ============================================
  // CUSTOMER PORTAL OPERATIONS
  // ============================================

  /**
   * Get contracts for customer portal (via backend for proper auth)
   */
  async getCustomerContracts(
    projectId: string,
    customerToken: string
  ): Promise<ServiceResult<Contract[]>> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/customer/${projectId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Customer-Token': customerToken,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        return { success: false, error: data.message || 'Failed to fetch contracts' };
      }

      return { success: true, data: data.contracts as Contract[] };
    } catch (error) {
      console.error('Error in getCustomerContracts:', error);
      return { success: false, error: 'Failed to fetch customer contracts' };
    }
  }

  // ============================================
  // v2 (CB-*) OPERATIONS
  // ============================================
  // Contract Builder & E-Signature v2 service methods. v2 model types come from
  // `@/types/contracts` (plural) where applicable; this method returns the existing
  // singular `Contract` extended with the v2 fields (CB-2.1 types extension).

  /**
   * CB-2.1: create a v2 draft contract from an uploaded PDF.
   * POSTs multipart/form-data (file + project_id) to the backend create-from-upload endpoint,
   * which resolves tenant from the project, re-validates the PDF limits (authority), stores the
   * file in the private `contracts` bucket, and inserts the v2 draft row. Backend validation
   * failures (AC2 server path) are surfaced as the message so the dropzone can show them inline.
   */
  async createFromUpload(projectId: string, file: File): Promise<ServiceResult<Contract>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/create-from-upload`, {
        method: 'POST',
        // NOTE: do not set Content-Type — the browser sets the multipart boundary itself.
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        // Backend returns a specific recoverable message (FastAPI HTTPException detail or {message}).
        return { success: false, error: data.detail || data.message || 'Failed to create contract' };
      }

      return { success: true, data: data.contract as Contract };
    } catch (error) {
      console.error('Error in createFromUpload:', error);
      return { success: false, error: 'Failed to create contract' };
    }
  }

  /**
   * CB-9.1: create a v2 DRAFT 'built' (from-scratch builder) contract for a project.
   * Mirrors createFromUpload minus the file — the backend resolves tenant from the project,
   * sets created_by server-side, stamps document_source='built', and seeds an empty block model.
   * Goes through the backend because contracts-table writes are admin-only under RLS (every v2
   * contracts write does), so a frontend-direct insert would fail for non-admin senders.
   */
  async createBuilt(projectId: string): Promise<ServiceResult<Contract>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/create-built`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        return { success: false, error: data.detail || data.message || 'Failed to create contract' };
      }
      return { success: true, data: data.contract as Contract };
    } catch (error) {
      console.error('Error in createBuilt:', error);
      return { success: false, error: 'Failed to create contract' };
    }
  }

  /** Short-lived signed URL for a v2 uploaded contract's source PDF (private bucket → via backend),
   *  so the field editor can re-open the document when resuming an existing draft. */
  async getContractSourceUrl(contractId: string): Promise<ServiceResult<string>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/source-url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success || !data.url) {
        return { success: false, error: data.detail || data.message || 'Could not open the document' };
      }
      return { success: true, data: data.url as string };
    } catch (error) {
      console.error('Error in getContractSourceUrl:', error);
      return { success: false, error: 'Could not open the document' };
    }
  }

  /**
   * CB-9.1: persist the from-scratch builder block model on a v2 DRAFT 'built' contract.
   * The model is whitelisted to the FR-12 node/mark set (lib/builderSchema.ts) BEFORE it is sent.
   * Backend enforces v2-only / built-only / draft-only and the tenant fence; a locked (sent)
   * contract returns a specific 409 message, surfaced verbatim.
   */
  async saveBuiltDocument(
    contractId: string,
    builtDocument: BuiltDocNode,
  ): Promise<ServiceResult<null>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/built-document`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ built_document: builtDocument }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        return { success: false, error: data.detail || data.message || 'Failed to save the document' };
      }
      return { success: true, data: null };
    } catch (error) {
      console.error('Error in saveBuiltDocument:', error);
      return { success: false, error: 'Failed to save the document' };
    }
  }

  /**
   * CB-2.8: replace a v2 DRAFT contract's source PDF (multipart). The backend enforces the rule by
   * envelope_status (draft → store + clear fields; sent/active → 409 locked) and on a locked/invalid
   * contract returns a specific message — surfaced verbatim, not swallowed into a generic error.
   */
  async replaceDocument(
    contractId: string,
    file: File,
  ): Promise<ServiceResult<{ cleared_fields?: number }>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(
        `${BACKEND_URL}/api/v1/contracts/${contractId}/replace-document`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }, // browser sets multipart boundary
          body: formData,
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        return { success: false, error: data.detail || data.message || 'Failed to replace document' };
      }
      return { success: true, data: { cleared_fields: data.cleared_fields } };
    } catch (error) {
      console.error('Error in replaceDocument:', error);
      return { success: false, error: 'Failed to replace document' };
    }
  }

  // ── v2 field placement (CB-2.3) — direct Supabase under tenant RLS (Enforcement Rule 1) ──

  /** List placed fields for a contract (read; tenant RLS scopes the rows). */
  async getContractFields(contractId: string): Promise<ServiceResult<ContractField[]>> {
    try {
      const { data, error } = await supabase
        .from('contract_fields')
        .select('*')
        .eq('contract_id', contractId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching contract fields:', error);
        return { success: false, error: error.message };
      }
      return { success: true, data: data as ContractField[] };
    } catch (error) {
      console.error('Error in getContractFields:', error);
      return { success: false, error: 'Failed to fetch contract fields' };
    }
  }

  /**
   * Create a placed field (CB-2.3). The payload is snake_case end-to-end (Enforcement Rule 2);
   * the insert runs as the authenticated tenant user under the existing contract_fields RLS
   * (Enforcement Rule 1) — never service_role. `field_type` must be a lowercase-snake CHECK value.
   */
  async createContractField(field: NewContractField): Promise<ServiceResult<ContractField>> {
    try {
      const { data, error } = await supabase
        .from('contract_fields')
        .insert(field)
        .select()
        .single();

      if (error) {
        console.error('Error creating contract field:', error);
        return { success: false, error: error.message };
      }
      return { success: true, data: data as ContractField };
    } catch (error) {
      console.error('Error in createContractField:', error);
      return { success: false, error: 'Failed to create contract field' };
    }
  }

  /**
   * Update a placed field's editable properties (CB-2.4: move/resize/configure). Snake_case patch
   * (Rule 2); runs under tenant RLS as the authenticated user (Rule 1).
   *
   * Note: CB-2.3 persists placement, so CB-2.4's manipulations persist too (move/resize/configure
   * must survive reload to stay coherent with placement) — a deliberate extension beyond the
   * CB-2.4 spec's "local-only" assumption, which predated CB-2.3's persistence.
   */
  async updateContractField(
    fieldId: string,
    patch: ContractFieldPatch,
  ): Promise<ServiceResult<ContractField>> {
    try {
      const { data, error } = await supabase
        .from('contract_fields')
        .update(patch)
        .eq('id', fieldId)
        .select()
        .single();

      if (error) {
        console.error('Error updating contract field:', error);
        return { success: false, error: error.message };
      }
      return { success: true, data: data as ContractField };
    } catch (error) {
      console.error('Error in updateContractField:', error);
      return { success: false, error: 'Failed to update contract field' };
    }
  }

  /** Delete a placed field (CB-2.4). Runs under tenant RLS as the authenticated user (Rule 1). */
  async deleteContractField(fieldId: string): Promise<ServiceResult<void>> {
    try {
      // `.select()` returns the rows actually deleted. Under RLS a delete the caller is not
      // permitted to make (no DELETE policy, or a row in another tenant) affects ZERO rows
      // WITHOUT raising an error — so we must assert a row came back, not just that `error` is
      // null. Trusting `!error` alone made delete a silent no-op (review finding B1).
      const { data, error } = await supabase
        .from('contract_fields')
        .delete()
        .eq('id', fieldId)
        .select('id');
      if (error) {
        console.error('Error deleting contract field:', error);
        return { success: false, error: error.message };
      }
      if (!data || data.length === 0) {
        console.error('Delete affected no rows (RLS denied or field already gone):', fieldId);
        return { success: false, error: 'Field could not be deleted' };
      }
      return { success: true };
    } catch (error) {
      console.error('Error in deleteContractField:', error);
      return { success: false, error: 'Failed to delete contract field' };
    }
  }

  /**
   * CB-2.7: advance a v2 draft through the send-readiness gate. The backend re-validates the two
   * readiness rules (every signer has a signature field; no unassigned field) and requires the
   * §7003 excluded-document attestation, recording it as an audit event. It does NOT dispatch —
   * actual send (Postmark, envelope→sent) is Epic 4. Returns the backend message on success, or a
   * specific rejection (unmet conditions / missing attestation) as the error.
   */
  async sendContract(
    contractId: string,
    attestation: { attestation_version: string; attestation_text: string },
    expiry?: { expiry_enabled: boolean; expiry_days: number | null },
  ): Promise<ServiceResult<{ message?: string }>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          excluded_document_attested: true,
          attestation_version: attestation.attestation_version,
          attestation_text: attestation.attestation_text,
          // CB-4.5: optional per-contract expiry (off by default). expiry_days null => backend 30-day default.
          ...(expiry?.expiry_enabled
            ? { expiry_enabled: true, expiry_days: expiry.expiry_days }
            : { expiry_enabled: false }),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        return { success: false, error: data.detail || data.message || 'Failed to send contract' };
      }
      return { success: true, data: { message: data.message } };
    } catch (error) {
      console.error('Error in sendContract:', error);
      return { success: false, error: 'Failed to send contract' };
    }
  }

  /**
   * CB-4.6 (FR-42): void a not-yet-completed v2 contract with a recorded reason. The backend flips
   * envelope_status→voided (disabling every signing link via the terminal-status gate) and audit-logs
   * the reason. Rejected (409) on completed/terminal contracts. Authenticated tenant action.
   */
  /**
   * CB-7.4: duplicate a v2 contract into a fresh draft (clones doc ref + fields + recipients; no
   * tokens/signed values/events). Returns the new draft contract. v2-only; the source is untouched.
   */
  async duplicate(contractId: string): Promise<ServiceResult<Contract>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        return { success: false, error: data.detail || data.message || 'Failed to duplicate contract' };
      }
      return { success: true, data: data.contract as Contract };
    } catch (error) {
      console.error('Error in duplicate:', error);
      return { success: false, error: 'Failed to duplicate contract' };
    }
  }

  /**
   * CB-10.1 (FR-18/FR-19): save a v2 contract (uploaded+fields or built+fields) as a tenant-global
   * template. The server derives the recipient roles + role-keyed field layout from the source
   * contract's real signers/fields and snapshots the document; the client supplies the name, cosmetic
   * `role_names`, captured `merge_tokens`, and an optional send-scope.
   */
  async saveContractAsTemplate(input: SaveTemplateInput): Promise<ServiceResult<ContractTemplate>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        return { success: false, error: data.detail || data.message || 'Failed to save template' };
      }
      return { success: true, data: data.template as ContractTemplate };
    } catch (error) {
      console.error('Error in saveContractAsTemplate:', error);
      return { success: false, error: 'Failed to save template' };
    }
  }

  /**
   * CB-10.2: list the tenant's (non-archived) contract templates for the "Use a template" picker.
   * Read DIRECTLY via supabase under the contract_templates tenant SELECT RLS (Rule 1) — no backend
   * round-trip needed for a read.
   */
  async listTemplates(includeArchived = false): Promise<ServiceResult<ContractTemplate[]>> {
    try {
      let query = supabase.from('contract_templates').select('*');
      // CB-10.4: the management tab can opt into archived (read-only); pickers never do.
      if (!includeArchived) query = query.eq('archived', false);
      // ⚠️ COM-R.95 P15. COM-2.10 made this table VERSIONED, and
      // `version_contract_template()` copies the name forward ON PURPOSE — a new
      // version is a change of terms, not of label. Without this filter the
      // picker showed two rows with the identical name and no way to tell them
      // apart, and `createFromTemplate` on the retired one succeeded, producing a
      // contract citing a version somebody had deliberately retired for having
      // the wrong terms. Nothing in the backend checks `superseded_at` either.
      query = query.is('superseded_at', null);
      const { data, error } = await query.order('name', { ascending: true });

      if (error) {
        console.error('Error listing contract templates:', error);
        return { success: false, error: error.message };
      }
      return { success: true, data: data as ContractTemplate[] };
    } catch (error) {
      console.error('Error in listTemplates:', error);
      return { success: false, error: 'Failed to load templates' };
    }
  }

  /**
   * CB-10.4 (review P7/P8): edit a template's metadata (name + send-role scope) via the BACKEND PUT —
   * admin-gated + `allowed_permission_roles` validated server-side against the tenant's real roles
   * (parity with save; closes the direct-supabase validation-bypass gap).
   */
  async updateTemplate(
    templateId: string,
    patch: { name?: string; allowed_permission_roles?: number[]; pipeline_types?: string[] },
  ): Promise<ServiceResult<ContractTemplate>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/templates/${templateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        return { success: false, error: data.detail || data.message || 'Failed to update template' };
      }
      return { success: true, data: data.template as ContractTemplate };
    } catch (error) {
      console.error('Error in updateTemplate:', error);
      return { success: false, error: 'Failed to update template' };
    }
  }

  /**
   * CB-10.4 (review P8): duplicate a template via the BACKEND — admin-gated, deep-copies the JSONB and
   * COPIES the PDF bytes to a new object (so the copy is independent, no shared storage object a later
   * hard-delete could reap).
   */
  async duplicateTemplate(templateId: string): Promise<ServiceResult<ContractTemplate>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/templates/${templateId}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        return { success: false, error: data.detail || data.message || 'Failed to duplicate template' };
      }
      return { success: true, data: data.template as ContractTemplate };
    } catch (error) {
      console.error('Error in duplicateTemplate:', error);
      return { success: false, error: 'Failed to duplicate template' };
    }
  }

  /**
   * CB-10.4: has this template ever produced a contract? Pre-flights the delete-confirm copy. Backend
   * (the usage check reads the admin-only `contracts` table, so it must run server-side).
   */
  async getTemplateUsage(templateId: string): Promise<ServiceResult<{ used: boolean }>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/templates/${templateId}/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { success: false, error: data.detail || data.message || 'Failed to check template usage' };
      }
      return { success: true, data: { used: data.used === true } };
    } catch (error) {
      console.error('Error in getTemplateUsage:', error);
      return { success: false, error: 'Failed to check template usage' };
    }
  }

  /**
   * CB-10.4 (FR-23): remove a template. The backend decides used→archive vs never-used→hard-delete
   * (server-side, since it must read the admin-only contracts table). Returns `archived` so the UI can
   * show the right outcome.
   */
  async deleteTemplate(templateId: string): Promise<ServiceResult<{ archived: boolean }>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/templates/${templateId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        return { success: false, error: data.detail || data.message || 'Failed to remove template' };
      }
      return { success: true, data: { archived: data.archived === true } };
    } catch (error) {
      console.error('Error in deleteTemplate:', error);
      return { success: false, error: 'Failed to remove template' };
    }
  }

  /** CB-10.2: fetch one template (tenant RLS) to pre-load its document + role-mapped fields. */
  async getTemplate(templateId: string): Promise<ServiceResult<ContractTemplate>> {
    try {
      const { data, error } = await supabase
        .from('contract_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (error) {
        console.error('Error fetching contract template:', error);
        return { success: false, error: error.message };
      }
      return { success: true, data: data as ContractTemplate };
    } catch (error) {
      console.error('Error in getTemplate:', error);
      return { success: false, error: 'Failed to load template' };
    }
  }

  /**
   * CB-10.2 (FR-20/FR-22): instantiate a v2 draft contract from a template. The backend snapshots the
   * template's document + role-keyed fields into the new contract, materializes signers from the
   * role→person map, and auto-populates merge variables from the project.
   */
  async createFromTemplate(
    templateId: string,
    input: CreateFromTemplateInput,
  ): Promise<ServiceResult<Contract>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/templates/${templateId}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        return { success: false, error: data.detail || data.message || 'Failed to create contract from template' };
      }
      return { success: true, data: data.contract as Contract };
    } catch (error) {
      console.error('Error in createFromTemplate:', error);
      return { success: false, error: 'Failed to create contract from template' };
    }
  }

  async voidContract(contractId: string, reason: string): Promise<ServiceResult<{ message?: string }>> {
    try {
      const token = await getAuthToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/void`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        return { success: false, error: data.detail || data.message || 'Failed to void contract' };
      }
      return { success: true, data: { message: data.message } };
    } catch (error) {
      console.error('Error in voidContract:', error);
      return { success: false, error: 'Failed to void contract' };
    }
  }

  /** List signers for a contract, signing order ascending (consumed by the editor legend). */
  async getContractSigners(contractId: string): Promise<ServiceResult<ContractSigner[]>> {
    try {
      const { data, error } = await supabase
        .from('contract_signers')
        .select('*')
        .eq('contract_id', contractId)
        .order('signing_order', { ascending: true });

      if (error) {
        console.error('Error fetching contract signers:', error);
        return { success: false, error: error.message };
      }
      return { success: true, data: data as ContractSigner[] };
    } catch (error) {
      console.error('Error in getContractSigners:', error);
      return { success: false, error: 'Failed to fetch contract signers' };
    }
  }

  /**
   * Add a recipient to a v2 contract (write → Python backend; CB-3.1).
   * name + email + type only — no account, no cap (FR-24/25). Duplicate email is allowed (FR-36).
   * The backend rejects legacy contracts (v2-only guard) and sets tenant_id / token server-side.
   */
  async addContractSigner(
    contractId: string,
    input: { name?: string | null; email: string; recipient_type: RecipientType; internal_user_id?: string | null }
  ): Promise<ServiceResult<ContractSigner>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };

      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/signers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, error: typeof err.detail === 'string' ? err.detail : 'Failed to add recipient' };
      }
      const data = await response.json();
      return { success: true, data: data as ContractSigner };
    } catch (error) {
      console.error('Error in addContractSigner:', error);
      return { success: false, error: 'Failed to add recipient' };
    }
  }

  /** Edit a recipient's name / email / type (write → Python backend; CB-3.1). */
  async updateContractSigner(
    contractId: string,
    signerId: string,
    patch: { name?: string | null; email?: string; recipient_type?: RecipientType; internal_user_id?: string | null }
  ): Promise<ServiceResult<ContractSigner>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };

      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/signers/${signerId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, error: typeof err.detail === 'string' ? err.detail : 'Failed to update recipient' };
      }
      const data = await response.json();
      return { success: true, data: data as ContractSigner };
    } catch (error) {
      console.error('Error in updateContractSigner:', error);
      return { success: false, error: 'Failed to update recipient' };
    }
  }

  /**
   * Persist signing_order across recipients (CB-3.2). Equal numbers = parallel, different =
   * sequential — the numbers are the source of truth; CB-3.5 is the engine that ENFORCES the
   * order (turn unlocking). This writes intent only — never gate signing here.
   *
   * Goes DIRECT to Supabase (not the backend), under the authenticated tenant session so RLS
   * scopes the write to the caller's tenant. This is deliberate: signing_order is the only column
   * touched and the backend SignerUpdate schema intentionally excludes it (CB-3.2 adds no backend
   * file). contract_signers rows exist only for v2 contracts, so the v2-only rule is structural.
   * Applied ATOMICALLY via the update_contract_signer_orders RPC (one transaction): a partial
   * failure can no longer leave a half-renumbered roster (two signers sharing an order, or a gap)
   * that would silently flip sequential↔parallel routing (review S5). The RPC runs SECURITY
   * INVOKER, so the tenant RLS policy and the CB-3.4 lock-after-send trigger still apply.
   */
  async updateSignerOrders(
    contractId: string,
    updates: Array<{ id: string; signing_order: number }>
  ): Promise<ServiceResult> {
    if (updates.length === 0) return { success: true };
    try {
      const { error } = await supabase.rpc('update_contract_signer_orders', {
        p_contract_id: contractId,
        p_orders: updates,
      });
      if (error) {
        console.error('Error updating signer order:', error);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (error) {
      console.error('Error in updateSignerOrders:', error);
      return { success: false, error: 'Failed to update signing order' };
    }
  }

  /**
   * Set the sender's pre-sign flag on their signer row (CB-3.3, FR-27). true = the sender
   * completes their signature before send (participation = pre_sign); false = sign-in-turn.
   * This bit is the durable seam CB-3.6/3.7 (apply) and Epic 4 (send-readiness) consume — this
   * only records intent; never apply a signature here.
   *
   * Direct Supabase under the authenticated-tenant session (RLS scopes the write; contract_signers
   * is v2-only by construction), mirroring the CB-3.2 signing_order write — the backend signer
   * schema intentionally omits is_presign, and CB-3.3 adds no backend file.
   */
  async updateSignerPresign(
    contractId: string,
    signerId: string,
    isPresign: boolean
  ): Promise<ServiceResult> {
    try {
      const { error } = await supabase
        .from('contract_signers')
        .update({ is_presign: isPresign })
        .eq('id', signerId)
        .eq('contract_id', contractId);
      if (error) {
        console.error('Error updating signer pre-sign flag:', error);
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (error) {
      console.error('Error in updateSignerPresign:', error);
      return { success: false, error: 'Failed to update participation' };
    }
  }

  /** Fetch the current user's stored signature (own row), or null if none (CB-3.6). */
  async getMySignature(): Promise<ServiceResult<StoredSignature | null>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };

      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/me/signature`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, error: typeof err.detail === 'string' ? err.detail : 'Failed to load signature' };
      }
      const data = await response.json();
      return { success: true, data: (data ?? null) as StoredSignature | null };
    } catch (error) {
      console.error('Error in getMySignature:', error);
      return { success: false, error: 'Failed to load signature' };
    }
  }

  /** Save (upsert) the current user's stored signature + authorization (CB-3.6). user_id/tenant_id
   *  are derived server-side; default-deny is empty allowed_roles/allowed_user_ids. */
  async saveMySignature(payload: SaveSignaturePayload): Promise<ServiceResult<StoredSignature>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };

      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/me/signature`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, error: typeof err.detail === 'string' ? err.detail : 'Failed to save signature' };
      }
      const data = await response.json();
      return { success: true, data: data as StoredSignature };
    } catch (error) {
      console.error('Error in saveMySignature:', error);
      return { success: false, error: 'Failed to save signature' };
    }
  }

  /** Server-computed signature affordances for a slot (CB-3.7). External slots → is_internal false. */
  async getSignatureOptions(contractId: string, signerId: string): Promise<ServiceResult<SignatureOptions>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const response = await fetch(
        `${BACKEND_URL}/api/v1/contracts/${contractId}/signers/${signerId}/signature-options`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, error: typeof err.detail === 'string' ? err.detail : 'Failed to load options' };
      }
      return { success: true, data: (await response.json()) as SignatureOptions };
    } catch (error) {
      console.error('Error in getSignatureOptions:', error);
      return { success: false, error: 'Failed to load signature options' };
    }
  }

  /** Apply an authorized teammate's stored signature to an internal slot (CB-3.7; internal-only). */
  async applyStoredSignature(contractId: string, signerId: string, userSignatureId: string): Promise<ServiceResult> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const response = await fetch(
        `${BACKEND_URL}/api/v1/contracts/${contractId}/signers/${signerId}/apply-stored-signature`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ user_signature_id: userSignatureId }),
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, error: typeof err.detail === 'string' ? err.detail : 'Failed to apply signature' };
      }
      return { success: true };
    } catch (error) {
      console.error('Error in applyStoredSignature:', error);
      return { success: false, error: 'Failed to apply signature' };
    }
  }

  /** Sign-now: the assigned internal user signs their OWN field before send (CB-3.7, AC3). */
  async signNow(
    contractId: string,
    signerId: string,
    value: { signature_image?: string | null; signature_typed?: string | null; signature_style?: string | null }
  ): Promise<ServiceResult> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };
      const response = await fetch(
        `${BACKEND_URL}/api/v1/contracts/${contractId}/signers/${signerId}/sign-now`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(value),
        },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, error: typeof err.detail === 'string' ? err.detail : 'Failed to sign' };
      }
      return { success: true };
    } catch (error) {
      console.error('Error in signNow:', error);
      return { success: false, error: 'Failed to sign' };
    }
  }

  /** Remove a recipient from a v2 contract (write → Python backend; CB-3.1). */
  async removeContractSigner(contractId: string, signerId: string): Promise<ServiceResult> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };

      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/${contractId}/signers/${signerId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, error: typeof err.detail === 'string' ? err.detail : 'Failed to remove recipient' };
      }
      return { success: true };
    } catch (error) {
      console.error('Error in removeContractSigner:', error);
      return { success: false, error: 'Failed to remove recipient' };
    }
  }

  /** CB-4.3 (AC2): tenant sending-domain deliverability for the warn-not-block banner. Tenant is
   *  derived server-side from the session. `authenticated === null` ⇒ unknown (do not warn). */
  async getDeliverabilityStatus(): Promise<ServiceResult<DeliverabilityStatus>> {
    try {
      const token = await getAuthToken();
      if (!token) return { success: false, error: 'Not authenticated' };

      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/deliverability/status`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return {
          success: false,
          error: typeof err.detail === 'string' ? err.detail : 'Failed to load deliverability status',
        };
      }
      const data = await response.json();
      return { success: true, data: data as DeliverabilityStatus };
    } catch (error) {
      console.error('Error in getDeliverabilityStatus:', error);
      return { success: false, error: 'Failed to load deliverability status' };
    }
  }

  /**
   * CB-5.1 — PUBLIC, no-auth loader for the v2 signing ceremony. Calls the token-scoped
   * `get_signing_context_v2` RPC (SECURITY DEFINER / service_role-equivalent, AD-9) with the
   * per-signer access_token straight off the signing link. Mirrors the legacy page's direct
   * `supabase.rpc('sign_contract', ...)` call — the anon Supabase client, no session — but is a
   * read-only LOAD: no signing act happens here (those are CB-5.5+).
   *
   * The RPC returns ONLY the calling signer's own data (token scoping); a `{ status: 'invalid' }`
   * payload (unknown/blank token or a non-v2 contract) is a successful call carrying the generic
   * invalid-link state — NOT a service error. A transport/RPC error returns success:false so the
   * page can show a recoverable "something went wrong" rather than a raw exception.
   */
  async getSigningContextV2(token: string): Promise<ServiceResult<SigningContextV2>> {
    try {
      const { data, error } = await supabase.rpc('get_signing_context_v2', { p_token: token });
      if (error) {
        console.error('Error loading signing context:', error);
        return { success: false, error: error.message };
      }
      // The RPC returns { status: 'ok', signer, contract, tenant } | { status: 'invalid' }. Validate
      // the shape before trusting the cast: a malformed 'ok' payload missing the nested objects would
      // otherwise crash deriveState (which destructures signer/contract) and white-screen the public
      // page. Coerce any unexpected shape to the safe 'invalid' state.
      const payload = data as { status?: string; signer?: unknown; contract?: unknown; tenant?: unknown } | null;
      if (!payload || typeof payload.status !== 'string') {
        return { success: true, data: { status: 'invalid' } as SigningContextV2 };
      }
      if (payload.status === 'ok' && (!payload.signer || !payload.contract || !payload.tenant)) {
        console.error('get_signing_context_v2 returned ok without signer/contract/tenant');
        return { success: true, data: { status: 'invalid' } as SigningContextV2 };
      }
      return { success: true, data: payload as SigningContextV2 };
    } catch (error) {
      console.error('Error in getSigningContextV2:', error);
      return { success: false, error: 'Failed to load signing context' };
    }
  }

  /**
   * CB-5.2 — PUBLIC signer self-attestation (FR-77). Posts the per-signer access_token to the
   * token-scoped backend endpoint (service_role), which stamps `self_attested_at` on the caller's
   * own row and records one append-only `self_attested` event. No session — the token is the only
   * credential; the attested name is resolved server-side (never sent by the client). Idempotent:
   * a re-confirm/resume returns success with `already_attested: true` and no duplicate event.
   */
  async selfAttestV2(
    token: string
  ): Promise<ServiceResult<{ self_attested_at: string | null; already_attested: boolean }>> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/public/v2/self-attest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: token }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return {
          success: false,
          error: typeof err.detail === 'string' ? err.detail : 'Failed to record your confirmation',
        };
      }
      const data = await response.json();
      return {
        success: true,
        data: {
          self_attested_at: (data?.self_attested_at ?? null) as string | null,
          already_attested: Boolean(data?.already_attested),
        },
      };
    } catch (error) {
      console.error('Error in selfAttestV2:', error);
      return { success: false, error: 'Failed to record your confirmation' };
    }
  }

  /**
   * CB-5.3 — PUBLIC ESIGN §7001(c) consent (FR-47/48). Posts the per-signer access_token + the
   * consent_version (which disclosure text was shown) to the token-scoped backend endpoint, which
   * stamps `consent_accepted_at` on the caller's own row and records one append-only
   * `consent_accepted` event (timestamp/IP/version + server-captured document reference). No
   * session. Idempotent on resume/double-tap (`already_consented: true`, no duplicate event).
   */
  async recordConsentV2(
    token: string,
    consentVersion: string
  ): Promise<ServiceResult<{ consent_accepted_at: string | null; already_consented: boolean }>> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/public/v2/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: token, consent_version: consentVersion }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return {
          success: false,
          error: typeof err.detail === 'string' ? err.detail : 'Failed to record your consent',
        };
      }
      const data = await response.json();
      return {
        success: true,
        data: {
          consent_accepted_at: (data?.consent_accepted_at ?? null) as string | null,
          already_consented: Boolean(data?.already_consented),
        },
      };
    } catch (error) {
      console.error('Error in recordConsentV2:', error);
      return { success: false, error: 'Failed to record your consent' };
    }
  }

  /**
   * CB-5.4 — PUBLIC token-scoped signed URL for the signer's contract PDF. The contracts bucket is
   * private; a public signer has no session, so the backend mints a time-limited signed URL scoped
   * to the access_token's own contract. `available: false` means the v2 contract has no source PDF
   * to render (the viewer then shows a graceful fallback).
   */
  async getDocumentUrlV2(
    token: string
  ): Promise<ServiceResult<{ available: boolean; url: string | null }>> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/public/v2/document-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: token }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return {
          success: false,
          error: typeof err.detail === 'string' ? err.detail : 'Failed to load the document',
        };
      }
      const data = await response.json();
      return {
        success: true,
        data: {
          available: Boolean(data?.available),
          url: (data?.url ?? null) as string | null,
        },
      };
    } catch (error) {
      console.error('Error in getDocumentUrlV2:', error);
      return { success: false, error: 'Failed to load the document' };
    }
  }

  /**
   * CB-5.6 — PUBLIC token-scoped read of the signer's OWN placed fields (for the guided-fill
   * overlay). Returns only fields assigned to the access_token's signer — never siblings' or
   * sender-prefill fields. Called directly via the anon Supabase client (no session).
   */
  async getSigningFieldsV2(token: string): Promise<ServiceResult<SigningFieldV2[]>> {
    try {
      const { data, error } = await supabase.rpc('get_signing_fields_v2', { p_token: token });
      if (error) {
        console.error('Error loading signing fields:', error);
        return { success: false, error: error.message };
      }
      const payload = data as { status?: string; fields?: SigningFieldV2[] } | null;
      if (!payload || payload.status !== 'ok') {
        return { success: false, error: 'This signing link isn\'t valid.' };
      }
      return { success: true, data: payload.fields ?? [] };
    } catch (error) {
      console.error('Error in getSigningFieldsV2:', error);
      return { success: false, error: 'Failed to load your fields' };
    }
  }

  /**
   * CB-5.6 — PUBLIC token-scoped write of ONE field the signer owns (manual fill + Date-Signed
   * auto-fill + applied signature). The RPC is fail-closed on terminal envelope/signer/expiry and
   * returns `forbidden` if the field isn't this signer's. `value` is the JSONB payload (snake_case).
   */
  async fillFieldV2(
    token: string,
    fieldId: string,
    value: Record<string, unknown>
  ): Promise<ServiceResult<{ filled_at: string | null }>> {
    try {
      const { data, error } = await supabase.rpc('rpc_fill_field', {
        p_token: token,
        p_field_id: fieldId,
        p_value: value,
      });
      if (error) {
        console.error('Error filling field:', error);
        return { success: false, error: error.message };
      }
      const payload = data as { status?: string; filled_at?: string } | null;
      if (!payload || payload.status !== 'ok') {
        const status = payload?.status;
        const message =
          status === 'closed'
            ? 'This signing link is no longer active.'
            : status === 'forbidden'
              ? "That field can't be filled from this link."
              : "We couldn't save that field. Please try again.";
        return { success: false, error: message };
      }
      return { success: true, data: { filled_at: payload.filled_at ?? null } };
    } catch (error) {
      console.error('Error in fillFieldV2:', error);
      return { success: false, error: 'Failed to save that field' };
    }
  }

  /**
   * CB-5.7 — PUBLIC decline-to-sign (FR-52/FR-31). Posts the per-signer access_token + an optional
   * reason to the token-scoped backend endpoint, which sets the signer `declined`, halts the
   * envelope via the CB-3.5 engine, records the `declined` audit event (IP/UA), and notifies the
   * sender. No session. The link is terminal after a successful decline.
   */
  async declineToSign(
    token: string,
    reason?: string
  ): Promise<ServiceResult<{ envelope_status: string }>> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/public/v2/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: token, reason: reason ?? null }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return {
          success: false,
          error: typeof err.detail === 'string' ? err.detail : 'Failed to decline',
        };
      }
      const data = await response.json();
      return { success: true, data: { envelope_status: (data?.envelope_status ?? 'declined') as string } };
    } catch (error) {
      console.error('Error in declineToSign:', error);
      return { success: false, error: 'Failed to decline' };
    }
  }

  /**
   * CB-5.8 — PUBLIC consent withdrawal (FR-75). Posts the per-signer access_token to the
   * token-scoped backend endpoint, which stamps `consent_withdrawn_at`, halts the signer's
   * electronic signing (server-side too), records one `consent_withdrawn` audit event, and notifies
   * the sender to arrange paper. Only valid for a signer who previously consented. No session.
   */
  async withdrawConsentV2(
    token: string
  ): Promise<ServiceResult<{ consent_withdrawn_at: string | null }>> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/public/v2/withdraw-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: token }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return {
          success: false,
          error: typeof err.detail === 'string' ? err.detail : 'Failed to withdraw consent',
        };
      }
      const data = await response.json();
      return { success: true, data: { consent_withdrawn_at: (data?.consent_withdrawn_at ?? null) as string | null } };
    } catch (error) {
      console.error('Error in withdrawConsentV2:', error);
      return { success: false, error: 'Failed to withdraw consent' };
    }
  }

  /**
   * CB-5.10 — PUBLIC Finish / signer-submit (FR-55/FR-56). Posts the per-signer access_token to the
   * token-scoped backend endpoint, which re-checks completeness server-side, sets the signer
   * `signed`+`signed_at`, records the per-act `signed` audit event (IP/UTC/UA/method/consent),
   * advances the envelope via the CB-3.5 engine, and — when this is the last signer — enqueues the
   * Epic 6 seal job. Never flips envelope_status='completed' (seal-worker-only). No session.
   */
  async signContractV2(
    token: string
  ): Promise<ServiceResult<{ signed_at: string | null; seal_enqueued: boolean; already_signed: boolean }>> {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/contracts/public/v2/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: token }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return {
          success: false,
          error: typeof err.detail === 'string' ? err.detail : 'Failed to finish signing',
        };
      }
      const data = await response.json();
      return {
        success: true,
        data: {
          signed_at: (data?.signed_at ?? null) as string | null,
          seal_enqueued: Boolean(data?.seal_enqueued),
          already_signed: Boolean(data?.already_signed),
        },
      };
    } catch (error) {
      console.error('Error in signContractV2:', error);
      return { success: false, error: 'Failed to finish signing' };
    }
  }
}

export const contractService = new ContractService();
